import React, { useMemo } from "react";
import { Image, Typography, Empty } from "antd";
import useIsMobile from "../../hooks/useIsMobile.js";
import { renderInlineMarkdown } from "./markdownInline.js";

const { Paragraph, Text } = Typography;

// Learn > Disciplines/Transportation/Custom Menus/Keyboard Shortcuts (imported cross-folder
// from BIMFramework/ rather than duplicated — see Learn/index.js) — a reference of the user's
// own ORD keyboard shortcuts, authored as "Keyboard Shortcuts.md" (## shortcuts-key **N**
// sections) plus one demo GIF per shortcut ("shortcuts-key-N.gif"). Generic content-folder
// rendering (markdown text block, then an unrelated stacked image list below it) would bury the
// 1:1 relationship between each shortcut's explanation and its own demo — so this pairs them,
// matching sections to images by the shared trailing number. `imageUrlFor` is passed in by
// whichever page renders this (same convention as MarkdownContent.js) rather than hardcoded to
// one API base path — this file has no opinion on which content-folder route serves its images.

function parseSections(text) {
  const lines = (text || "").split(/\r?\n/);
  const intro = [];
  const sections = [];
  let current = null;
  let sawHeading = false;

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    if (/^#\s+/.test(line)) continue; // page's own title — already shown above
    const heading = line.match(/^##\s+(.*)$/);
    if (heading) {
      sawHeading = true;
      if (current) sections.push(current);
      const numberMatch = heading[1].match(/(\d+)/);
      current = { heading: heading[1], number: numberMatch ? Number(numberMatch[1]) : null, lines: [] };
    } else if (sawHeading) {
      current.lines.push(line);
    } else {
      intro.push(line);
    }
  }
  if (current) sections.push(current);
  return { intro, sections };
}

function imageNumber(name) {
  const match = name.match(/(\d+)(?=\.[^.]+$)/);
  return match ? Number(match[1]) : null;
}

export default function KeyboardShortcutsView({ content, imageUrlFor }) {
  const isMobile = useIsMobile();
  const { intro, sections } = useMemo(() => parseSections(content?.description), [content]);
  const images = content?.images || [];

  const pairs = sections.map((section) => ({
    section,
    image: section.number != null ? images.find((img) => imageNumber(img.name) === section.number) : null,
  }));
  const pairedNames = new Set(pairs.map((p) => p.image?.name).filter(Boolean));
  const orphanImages = images.filter((img) => !pairedNames.has(img.name));

  if (sections.length === 0 && images.length === 0) {
    return <Empty description="No keyboard shortcuts documented yet" style={{ marginTop: 40 }} />;
  }

  return (
    <div>
      {intro.length > 0 && (
        <div className="ks-intro">
          {intro.map((line, i) => (
            <Paragraph key={i} style={{ margin: i === 0 ? "0 0 6px" : "6px 0 0", color: "rgba(255,255,255,0.75)", fontSize: 14, lineHeight: 1.7 }}>
              {renderInlineMarkdown(line)}
            </Paragraph>
          ))}
        </div>
      )}

      <style>{`
        .ks-intro {
          border-left: 3px solid #4f9cf9;
          background: rgba(79,156,249,0.06);
          border-radius: 0 10px 10px 0;
          padding: 16px 20px;
          margin: 20px 0 28px;
        }
        .ks-card {
          display: flex;
          align-items: center;
          gap: 28px;
          padding: 22px 0;
          border-top: 1px solid rgba(255,255,255,0.08);
        }
        .ks-card:first-of-type { border-top: none; }
        .ks-card.reverse { flex-direction: row-reverse; }
        .ks-media {
          flex: 0 0 auto;
          width: 340px;
          max-width: 42%;
          border-radius: 12px;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,0.1);
          background: rgba(255,255,255,0.02);
        }
        .ks-media img { width: 100%; display: block; }
        /* antd's Image wrapper div (.ant-image) defaults to display:inline-block, which sits
           on a text baseline and leaves a sliver of space below it — invisible against a plain
           background, but it pokes out past this container's rounded bottom corners since the
           wrapper itself (not just the <img> inside it) needs to be block-level to be clipped
           flush. The style prop on Image only reaches the inner img, not this wrapper, so
           it has to be targeted directly. */
        .ks-media .ant-image { display: block; }
        .ks-body { flex: 1; min-width: 0; display: flex; gap: 16px; align-items: flex-start; }
        .ks-key {
          flex-shrink: 0;
          width: 40px; height: 40px;
          display: flex; align-items: center; justify-content: center;
          border-radius: 8px;
          background: linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.03));
          border: 1px solid rgba(255,255,255,0.18);
          border-bottom: 3px solid rgba(255,255,255,0.18);
          font-family: 'DM Mono', monospace;
          font-weight: 700;
          font-size: 16px;
          color: #4f9cf9;
        }
      `}</style>

      {pairs.length > 0 && (
        <Image.PreviewGroup>
          <div>
            {pairs.map(({ section, image }, i) => (
              <div key={i} className={`ks-card${isMobile ? "" : i % 2 === 1 ? " reverse" : ""}`}
                style={isMobile ? { flexDirection: "column", alignItems: "stretch" } : undefined}>
                {image && (
                  <div className="ks-media" style={isMobile ? { width: "100%", maxWidth: "100%" } : undefined}>
                    <Image src={imageUrlFor(image)} style={{ width: "100%", display: "block" }} />
                  </div>
                )}
                <div className="ks-body">
                  {section.number != null && <div className="ks-key">{section.number}</div>}
                  <div>
                    {section.lines.map((line, j) => (
                      <Paragraph key={j} style={{ margin: j === 0 ? 0 : "6px 0 0", color: "rgba(255,255,255,0.75)", fontSize: 14, lineHeight: 1.7 }}>
                        {renderInlineMarkdown(line)}
                      </Paragraph>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {orphanImages.length > 0 && (
            <div style={{ marginTop: 28 }}>
              <Text type="secondary" style={{ fontSize: 12.5 }}>Additional images (no matching shortcut number)</Text>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 12 }}>
                {orphanImages.map((img) => (
                  <div key={img.name} className="ks-media" style={{ width: 220 }}>
                    <Image src={imageUrlFor(img)} style={{ width: "100%", display: "block" }} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </Image.PreviewGroup>
      )}
    </div>
  );
}
